import React from 'react';
import { t } from '../../utils/translations';
import { getFormattedUserName } from '../../utils/cvUtils';

export default function CVTemplateProfessional({ user, projects, language = 'id' }) {
  // Helper function to categorize education
  const categorizeEducation = (education) => {
    const higher = [];
    const k12 = [];
    
    education.forEach(edu => {
      const degree = edu.degree?.toLowerCase() || '';
      const isHigherEd = degree.includes('sarjana') || degree.includes('s1') || 
                        degree.includes('s2') || degree.includes('s3') || 
                        degree.includes('bachelor') || degree.includes('master') || 
                        degree.includes('magister') || degree.includes('phd') || 
                        degree.includes('doktor') || degree.includes('diploma');
      
      if (isHigherEd) {
        higher.push(edu);
      } else {
        k12.push(edu);
      }
    });
    
    return { higher, k12 };
  };

  const { higher: higherEducation, k12: k12Education } = user?.education ? 
    categorizeEducation(user.education) : { higher: [], k12: [] };

  return (
    <div className="cv-template avoid-break bg-white p-4 sm:p-6 md:p-8 max-w-[21cm] mx-auto shadow-sm print:p-8 print:shadow-none print:max-w-full" style={{ fontFamily: 'Georgia, serif' }}>
      {/* Professional Header with Blue Accent */}
      <div className="cv-section border-l-4 sm:border-l-8 border-blue-700 pl-3 sm:pl-6 mb-4 sm:mb-6 md:mb-8 avoid-break print:border-l-4 print:border-gray-800">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-1 sm:mb-2 text-left">{getFormattedUserName(user) || 'Your Name'}</h1>
        <p className="text-base sm:text-lg md:text-xl text-blue-700 font-semibold mb-2 sm:mb-3 print:text-gray-800">{user?.job || 'Professional Title'}</p>
        <div className="flex flex-wrap gap-2 sm:gap-4 text-xs sm:text-sm text-gray-600">
          {user?.email && <span className="flex items-center gap-1">✉️ {user.email}</span>}
          {user?.phone && <span className="flex items-center gap-1">📞 {user.phone}</span>}
          {user?.location && <span className="flex items-center gap-1">📍 {user.location}</span>}
        </div>
      </div>

      {/* Profile Photo - Professional Style */}
      {user?.photo && (
        <div className="cv-photo float-none sm:float-right mx-auto sm:ml-6 mb-4 w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 rounded-lg border-4 border-blue-700 overflow-hidden shadow-lg print:w-32 print:h-32 print:border-gray-800 print:shadow-none">
          <img src={user.photo} alt={user.full_name} className="w-full h-full object-cover" />
        </div>
      )}

      {/* Professional Summary */}
      {user?.summary && (
        <div className="cv-section mb-4 sm:mb-6 md:mb-8 avoid-break">
          <h2 className="cv-section-title text-base sm:text-lg md:text-xl font-bold text-blue-700 mb-2 sm:mb-3 pb-1 sm:pb-2 border-b-2 border-blue-700">
            {t('professionalSummary', language)}
          </h2>
          <p className="text-xs sm:text-sm text-gray-800 leading-relaxed text-justify">{user.summary}</p>
        </div>
      )}

      <div className="clear-both"></div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
        {/* Left Column */}
        <div className="col-span-1 space-y-4 sm:space-y-6">
          {/* Skills */}
          {user?.skills && user.skills.length > 0 && (
            <div className="cv-section avoid-break">
              <h2 className="cv-section-title text-base sm:text-lg font-bold text-blue-700 mb-2 sm:mb-3 pb-1 sm:pb-2 border-b-2 border-blue-700">
                {t('skills', language)}
              </h2>
              <ul className="space-y-1 sm:space-y-2">
                {user.skills.map((skill, i) => (
                  <li key={i} className="text-xs sm:text-sm text-gray-800 flex items-start gap-2">
                    <span className="text-blue-700 font-bold">▸</span>
                    <span>{skill}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Education - Higher Education */}
          {higherEducation.length > 0 && (
            <div className="cv-section avoid-break">
              <h2 className="cv-section-title text-base sm:text-lg font-bold text-blue-700 mb-2 sm:mb-3 pb-1 sm:pb-2 border-b-2 border-blue-700">
                {t('higherEducation', language)}
              </h2>
              <div className="space-y-3 sm:space-y-4">
                {higherEducation.map((edu, i) => (
                  <div key={i} className="cv-education-item avoid-break">
                    <h3 className="text-xs sm:text-sm font-bold text-gray-900">{edu.degree}</h3>
                    <p className="text-xs text-gray-700 italic">{edu.school || edu.institution}</p>
                    <p className="text-xs text-blue-700 font-semibold">{edu.year}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Education - K-12 */}
          {k12Education.length > 0 && (
            <div className="cv-section avoid-break">
              <h2 className="cv-section-title text-base sm:text-lg font-bold text-blue-700 mb-2 sm:mb-3 pb-1 sm:pb-2 border-b-2 border-blue-700">
                {t('formalEducation', language)}
              </h2>
              <div className="space-y-3 sm:space-y-4">
                {k12Education.map((edu, i) => (
                  <div key={i} className="cv-education-item avoid-break">
                    <h3 className="text-xs sm:text-sm font-bold text-gray-900">{edu.degree}</h3>
                    <p className="text-xs text-gray-700 italic">{edu.school || edu.institution}</p>
                    <p className="text-xs text-blue-700 font-semibold">{edu.year}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Experience */}
        <div className="col-span-1 sm:col-span-2 space-y-4 sm:space-y-6">
          {/* Professional Experience */}
          {user?.experience && user.experience.length > 0 && (
            <div className="cv-section avoid-break">
              <h2 className="cv-section-title text-base sm:text-lg md:text-xl font-bold text-blue-700 mb-3 sm:mb-4 pb-1 sm:pb-2 border-b-2 border-blue-700">
                {t('professionalExperience', language)}
              </h2>
              <div className="space-y-4 sm:space-y-5">
                {user.experience.map((exp, i) => (
                  <div key={i} className="cv-experience-item relative avoid-break">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-1 sm:mb-2">
                      <div>
                        <h3 className="text-sm sm:text-base font-bold text-gray-900">{exp.position}</h3>
                        <p className="text-xs sm:text-sm text-blue-700 font-semibold">{exp.company}</p>
                      </div>
                      <span className="text-xs text-gray-600 bg-blue-50 px-2 sm:px-3 py-1 rounded-full mt-1 sm:mt-0">
                        {exp.period}
                      </span>
                    </div>
                    {exp.description && (
                      <p className="text-xs sm:text-sm text-gray-800 leading-relaxed text-justify">
                        {exp.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Projects */}
          {projects && projects.length > 0 && (
            <div className="cv-section avoid-break">
              <h2 className="cv-section-title text-base sm:text-lg md:text-xl font-bold text-blue-700 mb-3 sm:mb-4 pb-1 sm:pb-2 border-b-2 border-blue-700">
                {t('keyProjects', language)}
              </h2>
              <div className="space-y-3 sm:space-y-4">
                {projects.map((proj, i) => (
                  <div key={i} className="cv-project-item bg-blue-50 rounded-lg p-3 sm:p-4 avoid-break">
                    <h3 className="text-xs sm:text-sm font-bold text-gray-900 mb-1">{proj.title}</h3>
                    <p className="text-xs sm:text-sm text-gray-700">{proj.description}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
